import { isPlatformServer } from '@angular/common';
import { Component, effect, inject, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { catchError, map, of } from 'rxjs';
import { HomePageData } from '../../../shared/models/marketing.models';
import { MarketingConsultationCtaComponent } from '../../components/marketing-consultation-cta.component';
import { MarketingCountryCardComponent } from '../../components/marketing-country-card.component';
import { MarketingGlobalSuccessStoriesComponent } from '../../components/marketing-global-success-stories.component';
import { MarketingPopupComponent } from '../../components/marketing-popup.component';
import { MarketingWhyChooseComponent } from '../../components/marketing-why-choose.component';
import { MarketingSuccessRoadmapComponent } from '../../components/marketing-success-roadmap.component';
import { MarketingFooterComponent } from '../../components/marketing-footer.component';
import { MarketingHeaderComponent } from '../../components/marketing-header.component';
import { MarketingHeroComponent } from '../../components/marketing-hero.component';
import { MarketingStatsStripComponent } from '../../components/marketing-stats-strip.component';
import { MarketingTestimonialsComponent } from '../../components/marketing-testimonials.component';
import { PagesService } from '../../../shared/services/pages.service';
import { buildPageTitle } from '../../../shared/constants/site-meta';
import { HOME_SLIDER_STORIES_LIMIT } from '../../components/success-stories/success-stories.utils';
import { resolveMilestoneCtaHref } from '../../utils/milestone-cta';

export type HomeLoadState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | { kind: 'ok'; home: HomePageData };

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [
    RouterLink,
    MarketingHeaderComponent,
    MarketingHeroComponent,
    MarketingStatsStripComponent,
    MarketingCountryCardComponent,
    MarketingGlobalSuccessStoriesComponent,
    MarketingWhyChooseComponent,
    MarketingSuccessRoadmapComponent,
    MarketingTestimonialsComponent,
    MarketingConsultationCtaComponent,
    MarketingFooterComponent,
    MarketingPopupComponent,
  ],
  templateUrl: './home-page.component.html',
})
export class HomePageComponent {
  private readonly pages = inject(PagesService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Loads in the component (not a route resolver) for smoother first paint.
   * On the server, failed fetches stay "pending" so we never SSR the error page
   * that would flash before the browser retries.
   */
  readonly homeState = toSignal(
    this.pages.getMarketingHome().pipe(
      map((home) => ({ kind: 'ok' as const, home })),
      catchError(() =>
        isPlatformServer(this.platformId)
          ? of({ kind: 'pending' as const })
          : of({ kind: 'error' as const }),
      ),
    ),
    { initialValue: { kind: 'pending' as const } satisfies HomeLoadState },
  );

  constructor() {
    effect(() => {
      const s = this.homeState();
      if (s.kind === 'ok') {
        this.applySeo(s.home);
      }
    });
  }

  destinationNames(home: HomePageData): string[] {
    return home.destinations.countries.map((c) => c.name);
  }

  testimonialItems(home: HomePageData) {
    return home.testimonials?.items ?? [];
  }

  successStoryItems(home: HomePageData) {
    return (home.successStories?.items ?? [])
      .slice(0, HOME_SLIDER_STORIES_LIMIT)
      .map((story) => ({
        ...story,
        ctaHref: resolveMilestoneCtaHref(story.ctaHref, story.title),
      }));
  }

  private applySeo(home: HomePageData): void {
    const seo = home.seo;
    const documentTitle = buildPageTitle(seo.title);
    this.title.setTitle(documentTitle);
    if (seo.description) this.meta.updateTag({ name: 'description', content: seo.description });
    if (seo.ogImage?.cdnUrl) {
      this.meta.updateTag({ property: 'og:image', content: seo.ogImage.cdnUrl });
      this.meta.updateTag({ property: 'og:title', content: documentTitle });
    }
  }
}
